/**
 * InsightsDashboardPage
 * Main Insights module page — four tabs:
 *   1. Dashboard     — usage stats
 *   2. Templates     — every template: its results, editor and runs
 *                      (components/templates/TemplatesTab)
 *   4. Agent         — Insights chat agent
 *   5. Feedback      — every like / dislike on the agents' answers
 *                      (insights.feedback_review)
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import '../components/InsightModals.css';
import '@modules/admin/pages/AdminPage.css';
import './InsightsDashboardPage.css';
import {
  ConfigProvider,
  theme as antdTheme,
} from 'antd';
import { useTheme } from '@/hooks/useTheme';
import { emitJourneyEvent, JOURNEY_EVENTS } from '@modules/journey/detect';

import {
  BarChartOutlined,
  RobotOutlined,
  AppstoreOutlined,
  FileTextOutlined,
  LikeOutlined,
} from '@ant-design/icons';
import { Sidebar } from '@/components/layout/Sidebar';
import { useLanguage } from '@/hooks/useLanguage';
import { useFeature } from '@/hooks/useFeature';
import { useInsightsData } from '../hooks/useInsightsData';
import { ScopeSelector } from '../components/ScopeSelector';
import { InsightsStickyBar, type FilterSlots } from '../components/InsightsStickyBar';
import { InsightsAgentPanel } from '../components/InsightsAgentPanel';
import { TemplatesTab } from '../components/templates/TemplatesTab';
import { PulseDashboard } from '../components/pulse/PulseDashboard';
import { CostBreakdownTab } from '../components/CostBreakdownTab';
import { ReportsTab } from '../components/reports/ReportsTab';
import { FeedbackReviewTab } from '../components/feedback/FeedbackReviewTab';
import insightsService from '../services/insightsService';
import type { AgentOption, InsightFilters, InsightsQuota, TopicsOverview } from '../types/insights';

// Computed FRESH on every page mount (via the useState initializer below) —
// not captured at module load. The old top-level-const version froze "today"
// to whenever the JS bundle first evaluated, so a dashboard kept open across
// midnight silently excluded all of today's traffic (dateTo = yesterday,
// end-of-day) — which users reported as "the page doesn't refresh". Same
// stale-"now" class of bug as ScopeSelector's buildRangePresets().
function makeDefaultFilters(): InsightFilters {
  return {
    tenantIds: [],
    chatbotIds: [],
    dateFrom: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    dateTo: dayjs().format('YYYY-MM-DD'),
  };
}

// ─── Main page ────────────────────────────────────────────────────────────────

function InsightsDashboardPageInner() {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  // Per-tenant gate for templates: creating and running them (the server
  // refuses both while it is off) and the Templates tab itself.
  const canCreateTopicTemplates = useFeature('insights.topic_templates');
  // Per-tenant feature flags that hide Insights sub-tabs entirely.
  // Super-admins can toggle these from the Tenants → Edit → Features panel.
  const canSeeCostBreakdown = useFeature('insights.cost_breakdown');
  const canSeeReport = useFeature('insights.report');
  const canSeeAgent = useFeature('insights.agent');
  const canSeeFeedback = useFeature('insights.feedback_review');
  const canSeeTemplateTabs = canCreateTopicTemplates;

  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ['dashboard', 'cost', 'templates', 'reports', 'agent', 'feedback'];
  // The two template tabs became one: their old links land on it.
  // ... and the old Report tab's links land on Reports.
  const requestedTab = ({ results: 'templates', management: 'templates', report: 'reports' } as Record<string, string>)[
    searchParams.get('tab') || ''] || searchParams.get('tab') || '';
  const activeTab = VALID_TABS.includes(requestedTab) ? requestedTab : 'dashboard';
  const setActiveTab = useCallback((tab: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (tab === 'dashboard') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
      }
      if (tab !== 'templates') {
        next.delete('template');
        next.delete('view');
      }
      if (tab !== 'reports') {
        next.delete('show');
        next.delete('report');
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const [filters, setFilters] = useState<InsightFilters>(makeDefaultFilters);
  // The range the page opened on: until the person picks other dates, the
  // Templates tab may widen it to where a template's answers are (item 70).
  const [openingRange] = useState(() => {
    const { dateFrom, dateTo } = makeDefaultFilters();
    return { dateFrom, dateTo };
  });
  const datesChosen = filters.dateFrom !== openingRange.dateFrom || filters.dateTo !== openingRange.dateTo;

  // Cost Breakdown's background-refetch state. Lifted into the dashboard
  // so the "Refreshing…" indicator can render inline with the filter row
  // (inside ScopeSelector) instead of as a separate pill in the tab.
  const [costTabRefreshing, setCostTabRefreshing] = useState(false);

  // Platform Pulse state
  const [topicsOverview, setTopicsOverview] = useState<TopicsOverview | null>(null);
  const [topicsOverviewLoading, setTopicsOverviewLoading] = useState(false);
  const [topicChatbotIds, setTopicChatbotIds] = useState<string[]>([]);
  const [topicTenantIds, setTopicTenantIds] = useState<string[]>([]);
  const [topicTopN, setTopicTopN] = useState(10);
  const [topicTemplateId, setTopicTemplateId] = useState<string | undefined>(
    () => localStorage.getItem('insights_topic_template_id') || undefined
  );

  // Quota state
  const [quota, setQuota] = useState<InsightsQuota | null>(null);
  const fetchQuota = useCallback(async () => {
    try {
      const q = await insightsService.getQuotaUsage();
      setQuota(q);
    } catch (err) {
      console.error('[InsightsDashboardPage] Failed to load quota:', err);
    }
  }, []);

  const {
    tenantTree,
    tenantTreeLoading,
    chatbots,
    chatbotsLoading,
    agentStats,
    agentStatsLoading,
    agentTimeseries,
    globalAgentStats,
    globalAgentTimeseries,
    globalAgentLoading,
    tenantRankings,
    tenantTimeseries,
    templates,
    templatesLoading,
    fetchAgentData,
    fetchGlobalAgentData,
    fetchTenantData,
    fetchTemplates,
    fetchInsightsBreakdown,
    refreshAll,
    reloadChatbots,
  } = useInsightsData();

  const fetchTopicsOverview = useCallback(async (
    currentFilters: InsightFilters,
    chatbotIds: string[],
    tenantIds: string[],
    topN: number,
    templateId?: string,
  ) => {
    setTopicsOverviewLoading(true);
    try {
      const data = await insightsService.getTopicsOverview({
        date_from: currentFilters.dateFrom,
        date_to: currentFilters.dateTo,
        tenant_ids: tenantIds.length > 0 ? tenantIds : (currentFilters.tenantIds.length > 0 ? currentFilters.tenantIds : undefined),
        chatbot_ids: chatbotIds.length > 0 ? chatbotIds.flatMap(id => id.split(',')) : (currentFilters.chatbotIds.length > 0 ? currentFilters.chatbotIds.flatMap(id => id.split(',')) : undefined),
        top_n: topN,
        template_id: templateId,
      });
      setTopicsOverview(data);
    } catch (err) {
      console.error('[InsightsDashboardPage] Failed to load topics overview:', err);
      setTopicsOverview(null);
    } finally {
      setTopicsOverviewLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    refreshAll(filters);
    fetchTemplates();
    fetchQuota();
    fetchTopicsOverview(filters, [], [], topicTopN, topicTemplateId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch on filter change
  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchAgentData(filters);
      fetchGlobalAgentData(filters);
      fetchTenantData(filters);
      fetchInsightsBreakdown(filters);
      fetchTopicsOverview(filters, topicChatbotIds, topicTenantIds, topicTopN, topicTemplateId);
    }
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch topics when topic-specific filters change
  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchTopicsOverview(filters, topicChatbotIds, topicTenantIds, topicTopN, topicTemplateId);
    }
  }, [topicChatbotIds, topicTenantIds, topicTopN, topicTemplateId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload chatbots when tenant filter changes
  useEffect(() => {
    reloadChatbots(filters.tenantIds.length > 0 ? filters.tenantIds : undefined);
  }, [filters.tenantIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group agents by NORMALIZED name (lower-cased + trimmed) so:
  //   - the same agent shared across tenants appears once
  //   - case-only differences like "agent builder" vs "Agent Builder"
  //     don't show as separate rows
  //   - accidental trailing whitespace doesn't split a real agent in two
  // The display label is the FIRST-seen casing (typically the original).
  const agentOptions = useMemo<AgentOption[]>(() => {
    const byKey = new Map<string, { label: string; ids: string[]; avatarColor?: string | null;
                                    avatarVariant?: number | null }>();
    chatbots.forEach(c => {
      const key = (c.chatbot_name || '').trim().toLowerCase();
      if (!key) return;
      const existing = byKey.get(key);
      if (existing) {
        existing.ids.push(c.chatbot_id);
      } else {
        // The first agent of a name lends the option its avatar.
        byKey.set(key, { label: c.chatbot_name.trim(), ids: [c.chatbot_id], avatarColor: c.avatar_color,
                         avatarVariant: c.avatar_variant });
      }
    });
    return Array.from(byKey.values())
      .map(({ label, ids, avatarColor, avatarVariant }) => ({ value: ids.join(','), label, avatarColor, avatarVariant }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [chatbots]);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    if (key === 'dashboard') {
      fetchAgentData(filters);
      fetchGlobalAgentData(filters);
      fetchTenantData(filters);
      fetchTopicsOverview(filters, topicChatbotIds, topicTenantIds, topicTopN, topicTemplateId);
    } else if (key === 'templates') {
      fetchTemplates();
      fetchQuota();
    }
  };

  // Settles once both are back (the Templates tab waits for it before it takes another change).
  const reloadTemplates = useCallback(() => Promise.all([fetchTemplates(), fetchQuota()]),
    [fetchTemplates, fetchQuota]);

  const handleTopicTemplateChange = useCallback((id: string | undefined) => {
    setTopicTemplateId(id);
    if (id) {
      localStorage.setItem('insights_topic_template_id', id);
    } else {
      localStorage.removeItem('insights_topic_template_id');
    }
  }, []);

  const scopeSelector = (
    <ScopeSelector
      tenantTree={tenantTree}
      agentOptions={agentOptions}
      agentOptionsLoading={chatbotsLoading}
      filters={filters}
      onChange={setFilters}
      loading={tenantTreeLoading}
      templates={templates}
      topicTemplateId={topicTemplateId}
      onTopicTemplateChange={handleTopicTemplateChange}
    />
  );

  const scopeSelectorSimple = (slots: FilterSlots = {}) => (
    <ScopeSelector
      {...slots}
      tenantTree={tenantTree}
      agentOptions={agentOptions}
      agentOptionsLoading={chatbotsLoading}
      filters={filters}
      onChange={setFilters}
      loading={tenantTreeLoading}
      isRefreshing={costTabRefreshing}
    />
  );

  // ─── Tab items ───────────────────────────────────────────────────────────────

  // (Previously a yellow historical-data banner shown for date ranges before
  // 2026-05-28 — removed per UX request.)
  const dashboardHistoricalBanner = null;

  const tabItems = [
    {
      key: 'dashboard',
      children: (
        <div>
          <InsightsStickyBar filters={scopeSelector} testId="dashboard-sticky" />
          {dashboardHistoricalBanner}
          <PulseDashboard
            agentStats={agentStats}
            agentTimeseries={agentTimeseries}
            globalAgentStats={globalAgentStats}
            globalAgentTimeseries={globalAgentTimeseries}
            globalAgentLoading={globalAgentLoading}
            tenantRankings={tenantRankings}
            tenantTimeseries={tenantTimeseries}
            chatbots={chatbots}
            tenantTree={tenantTree}
            topicsOverview={topicsOverview}
            topicsOverviewLoading={topicsOverviewLoading}
            filters={filters}
            loading={agentStatsLoading}
            templates={templates}
            onTopicFilterChange={(bots, tenants, n) => {
              setTopicChatbotIds(bots);
              setTopicTenantIds(tenants);
              setTopicTopN(n);
            }}
          />
        </div>
      ),
    },
    {
      key: 'cost',
      children: (
        <div>
          {/* Cost Breakdown doesn't use insight templates — drop the topic-
              template dropdown from this tab. It was confusing UI: visible
              but had no effect on any cost number.
              The filters stay at the top while the long breakdown scrolls
              (the page's one sticky bar, item 71). */}
          <InsightsStickyBar filters={scopeSelectorSimple()} testId="cost-sticky" />
          <CostBreakdownTab filters={filters} onLoadingChange={setCostTabRefreshing} />
        </div>
      ),
    },
    {
      key: 'templates',
      children: (
        <div>
          <TemplatesTab
            scope={scopeSelectorSimple}
            templates={templates}
            loading={templatesLoading}
            quota={quota}
            tenantTree={tenantTree}
            agentOptions={agentOptions}
            filters={filters}
            datesChosen={datesChosen}
            onRange={(range) => setFilters((f) => ({ ...f, ...range }))}
            canCreate={canCreateTopicTemplates}
            onChanged={reloadTemplates}
          />
        </div>
      ),
    },
    {
      key: 'reports',
      children: <ReportsTab tenantTree={tenantTree} agentOptions={agentOptions} />,
    },
    {
      key: 'feedback',
      children: <FeedbackReviewTab />,
    },
    {
      key: 'agent',
      children: (
        <div
          style={{
            position: 'relative',
            flex: 1,
            minHeight: 0,
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              overflow: 'hidden',
              borderRadius: 12,
              border: '1px solid var(--claude-border)',
            }}
          >
            <InsightsAgentPanel />
          </div>
        </div>
      ),
    },
  ];

  // Apply per-tenant feature flags — hide tabs whose feature is OFF.
  // Map: tab key → flag. If the flag is FALSE, the tab is removed entirely
  // (super-admins can re-enable via Tenants → Edit → Features panel).
  const tabFlagGates: Record<string, boolean> = {
    cost: canSeeCostBreakdown,
    templates: canSeeTemplateTabs,
    report: canSeeReport,
    agent: canSeeAgent,
    feedback: canSeeFeedback,
  };
  const visibleTabItems = tabItems.filter((ti) =>
    tabFlagGates[ti.key] === undefined ? true : tabFlagGates[ti.key]
  );
  // If the user's currently-active tab just got hidden by a flag change,
  // bounce them to the first remaining tab so the body doesn't go blank.
  useEffect(() => {
    if (!visibleTabItems.find((ti) => ti.key === activeTab) && visibleTabItems.length > 0) {
      setActiveTab(visibleTabItems[0].key as any);
    }
  }, [visibleTabItems.map((t) => t.key).join(','), activeTab]);

  // The tab row is one line that scrolls sideways when it does not fit; the
  // active tab stays in view (InsightsDashboardPage.css).
  const tabsRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    tabsRef.current?.querySelector<HTMLElement>('.tab-btn.active')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeTab]);

  // Lock body scroll when agent tab is active to prevent page-level scrolling
  useEffect(() => {
    if (activeTab === 'agent' || activeTab === 'feedback') {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [activeTab]);

  // The tab row: each tab's icon, words and flag. The Cost tab reuses the bar chart icon
  // (Ant ships no $ icon in the imports above).
  const tabButtons = [
    { key: 'dashboard', visible: true, Icon: BarChartOutlined,
      label: t('insights.tabs.dashboard') },
    { key: 'cost', visible: canSeeCostBreakdown, Icon: BarChartOutlined,
      label: t('costSimulator.chart.title') },
    { key: 'templates', visible: canSeeTemplateTabs, Icon: AppstoreOutlined,
      label: t('insights.tabs.templates') },
    { key: 'reports', visible: canSeeReport, Icon: FileTextOutlined,
      label: t('insights.tabs.reports') },
    { key: 'feedback', visible: canSeeFeedback, Icon: LikeOutlined,
      label: t('insights.tabs.feedback') },
    { key: 'agent', visible: canSeeAgent, Icon: RobotOutlined,
      label: t('insights.tabs.agent') },
  ];

  return (
    <ConfigProvider theme={{
      algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      token: { colorPrimary: '#3b82f6', colorPrimaryHover: '#2563eb', colorPrimaryActive: '#1d4ed8' },
    }}>
      <div className="admin-container">
        <Sidebar />

        <div className="main-container">
          <main className="main-content">
            {/* Header — same as AdminPage */}
            <div className="simple-chat-header">
              <h1 dir="auto" className="bot-title">{t('insights.title')}</h1>
              <p className="text-muted">{t('insights.subtitle')}</p>
            </div>

            {/* Tab Navigation — same as AdminPage */}
            <div className="admin-tabs insights-tabs" ref={tabsRef}>
              {/* One button per tab; it looks active only when it IS the tab shown (item 86). */}
              {tabButtons.filter((tab) => tab.visible).map((tab) => (
                <button key={tab.key} type="button" aria-current={tab.key === activeTab ? 'page' : undefined}
                        className={`tab-btn${tab.key === activeTab ? ' active' : ''}`}
                        onClick={() => handleTabChange(tab.key)} data-testid={`insights-tab-${tab.key}`}>
                  <tab.Icon style={{ marginInlineEnd: 6 }} />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div
              className="tab-content active"
              style={{
                overflow: activeTab === 'agent' || activeTab === 'feedback' ? 'hidden' : 'auto',
                minHeight: 0,
              }}
            >
              {visibleTabItems.find(ti => ti.key === activeTab)?.children}
            </div>
          </main>
        </div>
      </div>

    </ConfigProvider>
  );
}

// The boundary itself has to stay a class (only classes can catch), so the
// error UI lives here where a hook is legal.
function InsightsErrorView({ error, onRetry }: { error: Error; onRetry: () => void }) {
  const { t } = useLanguage();
  return (
    <div style={{ padding: 40, color: '#dc2626' }}>
      <h2>{t('insights.pageError')}</h2>
      <pre style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{error.message}</pre>
      <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, color: '#64748b' }}>{error.stack}</pre>
      <button onClick={onRetry} style={{ marginTop: 16 }}>{t('common.retry')}</button>
    </div>
  );
}

class InsightsErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[InsightsErrorBoundary]', error, info.componentStack);
  }
  render() {
    if (this.state.error) {
      return <InsightsErrorView error={this.state.error} onRetry={() => this.setState({ error: null })} />;
    }
    return this.props.children;
  }
}

export default function InsightsDashboardPage() {
  // Journey detection: arriving here and rendering the dashboard IS the
  // "know your brain" Try — there is no sub-gesture to wait for.
  useEffect(() => {
    emitJourneyEvent(JOURNEY_EVENTS.insightsViewed);
  }, []);
  return (
    <InsightsErrorBoundary>
      <InsightsDashboardPageInner />
    </InsightsErrorBoundary>
  );
}
